import {
  addDoc,
  arrayRemove,
  arrayUnion,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore"
import {
  customerDoc,
  customersCollection,
  getDb,
  sessionDoc,
  sessionsCollection,
  toCustomer,
} from "@/lib/firebase/firestore"
import { NOT_REMOVED } from "@/types"
import { pendingAsNow } from "@/utils/timestamps"
import { stripUndefined } from "@/utils/firestoreWrite"
import type { Customer, CustomerInput, Removable, RemovalKind } from "@/types"

/*
 * Customers, and who may see them.
 *
 * WHO MAY CALL WHAT — enforced by the Firestore rules as well as here.
 *
 *   Any signed-in facilitator: createCustomer
 *   Members of that customer:  listCustomersForFacilitator, updateCustomer,
 *                              shareCustomer, unshareCustomer,
 *                              removeCustomer, restoreCustomer
 *
 * The calls that fan out to sessions take the CALLER's uid as well as whoever
 * they are acting on, because the sessions have to be found by the caller's own
 * membership — see `syncCustomerToSessions`.
 *
 * Membership is the many-to-many join between facilitators and customers, and it
 * lives in `customer.facilitatorIds`. Every read below is scoped by it. There is
 * deliberately no "list every customer" call: that was the hole — the customer
 * list read the whole collection, so every facilitator saw every customer the
 * company had ever run a workshop for.
 */

const EMPTY_CUSTOMER: Omit<
  Customer,
  "id" | "createdAt" | "updatedAt" | "facilitatorIds" | keyof Removable
> = {
  name: "",
  sector: "",
  size: "",
  about: "",
}

/**
 * Creates a customer owned by the facilitator who made it.
 *
 * Their uid goes in `facilitatorIds` in the same write, not a second one: a
 * customer created without a member is a document nobody — including its author
 * — can ever read back, because every rule and every query is scoped by
 * membership. There is no repair path for one, so it must not be possible to
 * create.
 */
export async function createCustomer(
  facilitatorId: string,
  input: CustomerInput = {}
): Promise<string> {
  const ref = await addDoc(customersCollection(), {
    ...EMPTY_CUSTOMER,
    ...input,
    facilitatorIds: [facilitatorId],
    ...NOT_REMOVED,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
  return ref.id
}

/**
 * Every customer this facilitator may see: the ones they created and the ones
 * shared with them, which membership does not distinguish.
 *
 * Sorted client-side rather than with `orderBy("createdAt")`: a server timestamp
 * is null until acknowledged, so a just-created customer would sort to the wrong
 * end (or be excluded outright, since Firestore omits documents missing the
 * ordered field) and then jump once confirmed.
 *
 * Archived and deleted customers are RETURNED, not filtered out.
 *
 * They have to be: the Archive screen reads the same list, and the rules admit
 * exactly one customer query — `array-contains facilitatorIds`. Adding
 * `where("removedAs", "==", null)` would need a composite index and would still
 * leave the Archive without a query of its own. The split happens once, in
 * `useFacilitatorIndex`, so every screen that shows customers drops the archived
 * ones at the same moment and none of them can forget to.
 */
export async function listCustomersForFacilitator(facilitatorId: string): Promise<Customer[]> {
  const snapshot = await getDocs(
    query(customersCollection(), where("facilitatorIds", "array-contains", facilitatorId))
  )
  return snapshot.docs
    .map(toCustomer)
    .filter((c): c is Customer => c !== null)
    .sort((a, b) => pendingAsNow(b.createdAt) - pendingAsNow(a.createdAt))
}

export async function updateCustomer(
  customerId: string,
  facilitatorId: string,
  input: CustomerInput
): Promise<void> {
  await updateDoc(customerDoc(customerId), {
    ...stripUndefined(input),
    updatedAt: serverTimestamp(),
  })
  // The name is stamped onto every session so the room can be told who the
  // workshop is for without reading this document. Renaming has to carry.
  if (input.name !== undefined) {
    await syncCustomerToSessions(customerId, facilitatorId, { name: input.name })
  }
}

/* -------------------------------------------------------------- membership */

/**
 * Gives another facilitator access to this customer and everything run for it.
 *
 * `arrayUnion` rather than a read-then-write: Firestore applies it server-side,
 * so two people sharing with two different colleagues at the same moment both
 * land. A read-modify-write of the whole array would lose one of them.
 */
export async function shareCustomer(
  customerId: string,
  facilitatorId: string,
  callerId: string
): Promise<void> {
  await updateDoc(customerDoc(customerId), {
    facilitatorIds: arrayUnion(facilitatorId),
    updatedAt: serverTimestamp(),
  })
  await syncCustomerToSessions(customerId, callerId, { add: facilitatorId })
}

/**
 * Takes access away again.
 *
 * Transactional, and refuses to remove the last member: a customer with an empty
 * `facilitatorIds` is unreadable by everyone for ever — no query returns it and
 * no rule admits it — so this is the one membership change that cannot be
 * undone by the person who made it. `arrayRemove` alone could not see that
 * coming; the read is the point.
 *
 * It also refuses to remove the CALLER, for a subtler version of the same
 * problem. Removing yourself lands on the customer and then strands the fan-out:
 * the sessions still carry your uid, and `session.facilitatorIds` is what the
 * rules test — so you would keep the access you just gave up, on every session
 * this customer has, with nothing left able to read the customer to correct
 * them. Leaving is a thing someone else does for you.
 */
export async function unshareCustomer(
  customerId: string,
  facilitatorId: string,
  callerId: string
): Promise<void> {
  if (facilitatorId === callerId) {
    throw new Error("You cannot remove your own access. Ask another facilitator to do it.")
  }

  await runTransaction(getDb(), async (tx) => {
    const ref = customerDoc(customerId)
    const snapshot = await tx.get(ref)
    if (!snapshot.exists()) throw new Error(`Customer ${customerId} does not exist`)

    const members = toCustomer(snapshot)!.facilitatorIds
    if (!members.includes(facilitatorId)) return
    if (members.length <= 1) {
      throw new Error("A customer needs at least one facilitator. Share it before leaving.")
    }

    tx.update(ref, { facilitatorIds: arrayRemove(facilitatorId), updatedAt: serverTimestamp() })
  })
  await syncCustomerToSessions(customerId, callerId, { remove: facilitatorId })
}

/**
 * What a change to a customer has to carry onto its sessions.
 *
 * One operation, not a snapshot of the result. See `syncCustomerToSessions`.
 */
type CustomerFanOut = { add: string } | { remove: string } | { name: string }

/**
 * Carries a customer change onto each of its sessions.
 *
 * Sessions carry their own `facilitatorIds` because that is what the rules test
 * on every read and write of a session and everything under it — pain points,
 * jobs, picks. Testing the customer instead would cost an extra document read
 * per operation, on collections a whole room writes to at once.
 *
 * The copy is what makes that affordable, and this is what keeps it true. It is
 * a fan-out, so it is deliberately NOT part of the membership write: a batch
 * that failed would otherwise roll back the share itself, and a share that
 * landed with one stale session is a smaller problem than a share that did not
 * land at all. Batches are chunked because Firestore caps them at 500 writes.
 *
 * **It applies the OPERATION, never the resulting array.** Writing
 * `facilitatorIds` wholesale from a read of the customer loses concurrent
 * changes to the same field: a rename reads `[A, B]`, an unshare then removes B
 * and fans `[A]` out, and the slower rename batch writes `[A, B]` back over it —
 * restoring, on every session, the access somebody just revoked. `arrayUnion`
 * and `arrayRemove` are applied server-side against whatever each session
 * currently holds, so the two changes compose in either order and neither can
 * undo the other. A rename touches only `customerName` for the same reason: it
 * has no business writing membership at all.
 *
 * The sessions are found by the CALLER's membership, not by `customerId`.
 * Firestore evaluates a list rule against the query's potential result set
 * rather than the documents it returns, so `where("customerId", "==", …)` alone
 * cannot satisfy a rule that tests `facilitatorIds` — the query is refused
 * outright, and this whole fan-out with it. The consequence is that a session
 * whose copy has already lost the caller cannot be repaired from here; that is
 * the backfill noted in CLAUDE.md, and it is why nothing may create a session
 * with a membership that does not match its customer's.
 */
export async function syncCustomerToSessions(
  customerId: string,
  facilitatorId: string,
  change: CustomerFanOut
): Promise<void> {
  const snapshot = await getDocs(
    query(sessionsCollection(), where("facilitatorIds", "array-contains", facilitatorId))
  )
  const ids = snapshot.docs
    .filter((entry) => entry.get("customerId") === customerId)
    .map((entry) => entry.id)

  const patch =
    "add" in change
      ? { facilitatorIds: arrayUnion(change.add) }
      : "remove" in change
        ? { facilitatorIds: arrayRemove(change.remove) }
        : { customerName: change.name }

  for (let i = 0; i < ids.length; i += 400) {
    const batch = writeBatch(getDb())
    for (const sessionId of ids.slice(i, i + 400)) {
      batch.update(sessionDoc(sessionId), { ...patch, updatedAt: serverTimestamp() })
    }
    await batch.commit()
  }
}

/* ----------------------------------------------------------------- archive */

/**
 * Takes a customer out of the working lists — archived, or deleted as a mistake.
 */
export async function removeCustomer(
  customerId: string,
  callerId: string,
  kind: RemovalKind
): Promise<void> {
  await updateDoc(customerDoc(customerId), {
    removedAt: serverTimestamp(),
    removedAs: kind,
    removedBy: callerId,
    updatedAt: serverTimestamp(),
  })
}

/**
 * Puts it back, whichever way it went.
 *
 * One call for both kinds: "restore" is the same write either way, and a caller
 * having to name the kind it is undoing could get it wrong against a customer
 * somebody else moved in between. The ARCHIVE is the only place that offers it —
 * the Deleted shelf has no Restore, deliberately — so this being able to undo a
 * delete is a property of the write, not a route anything renders.
 *
 * Its sessions come back with it — the ones that were only hidden BY it. A
 * session with its own removal keeps it.
 */
export async function restoreCustomer(customerId: string): Promise<void> {
  await updateDoc(customerDoc(customerId), {
    ...NOT_REMOVED,
    updatedAt: serverTimestamp(),
  })
}
